import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { DateRangePicker } from "@/components/common/DateRangePicker";
import { GuesteriaPartnersFilterButton } from "@/components/pages/guesteria-partners/components/GuesteriaPartnersFilterButton";
import {
  PartnerProviderLogsFilters,
  PartnerProviderLogEventType,
  PartnerProviderLogLevel,
  PartnerProviderLogStatus,
} from "@/types/partner-provider-log";
import type { DateRange } from "@/hooks/useDateRange";

interface LogsFiltersDraft {
  eventType: string;
  status: string;
  level: string;
  dateRange: DateRange;
}

const DEFAULT_DRAFT: LogsFiltersDraft = {
  eventType: "all",
  status: "all",
  level: "all",
  dateRange: { from: undefined, to: undefined },
};

function countActiveFilters(draft: LogsFiltersDraft): number {
  let count = 0;
  if (draft.eventType !== "all") count++;
  if (draft.status !== "all") count++;
  if (draft.level !== "all") count++;
  if (draft.dateRange.from || draft.dateRange.to) count++;
  return count;
}

function filtersToDate(dateString: string | undefined): Date | undefined {
  if (!dateString) return undefined;
  return new Date(dateString);
}

function appliedToDate(filters: PartnerProviderLogsFilters): LogsFiltersDraft {
  return {
    eventType: filters.eventType ?? "all",
    status: filters.status ?? "all",
    level: filters.level ?? "all",
    dateRange: {
      from: filtersToDate(filters.dateFrom),
      to: filtersToDate(filters.dateTo),
    },
  };
}

export interface PartnerProviderLogsFiltersPopoverProps {
  filters: PartnerProviderLogsFilters;
  onFiltersChange: (filters: PartnerProviderLogsFilters) => void;
}

export function PartnerProviderLogsFiltersPopover({
  filters,
  onFiltersChange,
}: PartnerProviderLogsFiltersPopoverProps): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<LogsFiltersDraft>(DEFAULT_DRAFT);

  const activeCount = countActiveFilters(appliedToDate(filters));

  const syncDraft = React.useCallback(() => {
    setDraft(appliedToDate(filters));
  }, [filters]);

  const handleApply = () => {
    onFiltersChange({
      ...filters,
      eventType: draft.eventType === "all" ? undefined : draft.eventType,
      status: draft.status === "all" ? undefined : draft.status,
      level: draft.level === "all" ? undefined : draft.level,
      dateFrom: draft.dateRange.from
        ? format(draft.dateRange.from, "yyyy-MM-dd")
        : undefined,
      dateTo: draft.dateRange.to
        ? format(draft.dateRange.to, "yyyy-MM-dd")
        : undefined,
      page: 1,
    });
    setOpen(false);
  };

  const handleReset = () => {
    setDraft(DEFAULT_DRAFT);
    onFiltersChange({
      ...filters,
      eventType: undefined,
      status: undefined,
      level: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      page: 1,
    });
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) syncDraft();
      }}
    >
      <PopoverTrigger asChild>
        <GuesteriaPartnersFilterButton>
          {activeCount > 0
            ? t("partners.admin.filters.popover.titleWithCount", {
                count: activeCount,
              })
            : t("partners.admin.filters.popover.title")}
        </GuesteriaPartnersFilterButton>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={8}
        className="flex w-[300px] flex-col gap-4 rounded-2xl border border-[#E5E5E5] bg-white p-4 shadow-[0px_3px_3px_0px_rgba(0,0,0,0.12)]"
      >
        <div className="flex items-center justify-between">
          <h4 className="text-base font-semibold leading-normal text-[#171717]">
            {activeCount > 0
              ? t("partners.admin.filters.popover.titleWithCount", {
                  count: activeCount,
                })
              : t("partners.admin.filters.popover.title")}
          </h4>
          <Button
            type="button"
            variant="span"
            className="rounded p-1 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900"
            onClick={() => setOpen(false)}
            aria-label={t("partners.admin.filters.popover.closeAria")}
          >
            <X className="size-5" />
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>{t("partners.admin.filters.eventType")}</Label>
            <Select
              value={draft.eventType}
              onValueChange={(value) =>
                setDraft((prev) => ({ ...prev, eventType: value }))
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("partners.admin.filters.allEventTypes")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("partners.admin.filters.allEventTypes")}
                </SelectItem>
                {Object.values(PartnerProviderLogEventType).map((et) => (
                  <SelectItem key={et} value={et}>
                    {t(`partners.admin.eventTypeValues.${et}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t("partners.admin.filters.status")}</Label>
            <Select
              value={draft.status}
              onValueChange={(value) =>
                setDraft((prev) => ({ ...prev, status: value }))
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("partners.admin.filters.allStatuses")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("partners.admin.filters.allStatuses")}
                </SelectItem>
                {Object.values(PartnerProviderLogStatus).map((s) => (
                  <SelectItem key={s} value={s}>
                    {t(`partners.admin.logStatusValues.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t("partners.admin.filters.level")}</Label>
            <Select
              value={draft.level}
              onValueChange={(value) =>
                setDraft((prev) => ({ ...prev, level: value }))
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("partners.admin.filters.allLevels")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("partners.admin.filters.allLevels")}
                </SelectItem>
                {Object.values(PartnerProviderLogLevel).map((l) => (
                  <SelectItem key={l} value={l}>
                    {t(`partners.admin.logLevelValues.${l}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>{t("partners.admin.filters.dateRange")}</Label>
            <DateRangePicker
              dateRange={draft.dateRange}
              onDateRangeChange={(range) =>
                setDraft((prev) => ({ ...prev, dateRange: range }))
              }
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-8 rounded-md border-[#E5E5E5] bg-white px-4 text-sm font-medium text-zinc-900 shadow-none hover:bg-zinc-50"
            onClick={handleReset}
          >
            {t("partners.admin.filters.popover.reset")}
          </Button>
          <Button
            type="button"
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm font-medium leading-normal text-[#FAFAFA] hover:bg-[#5F7597]"
            onClick={handleApply}
          >
            {t("partners.admin.filters.popover.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
